/**
 * Снимки экранов для переходов (см. layouts/ScreenFrame).
 *
 * Роутер снимает уходящий экран и ставит новый в один момент, поэтому обе
 * стороны перехода одновременно в дереве не бывают. В момент смены записи
 * истории DOM ещё старый: область перехода копируется вместе с позициями
 * прокрутки и значениями полей (их `cloneNode` не переносит). Копия неживая —
 * `inert`, без идентификаторов, автофокуса, анимаций и обработчиков — и живёт
 * ровно столько, сколько идёт анимация.
 *
 * Область перехода — `[data-nav-area]` (в оболочке это колонка с экраном и
 * полосами над ним: баннер установки, N-03, SYS-16), а без неё — сам экран
 * `[data-nav-screen]`. Полосы живут вне экрана и на вложенных экранах
 * скрыты; если бы копировался только экран, в момент перехода они пропадали
 * бы или выскакивали рывком, а снимок прыгал на их высоту.
 *
 * Снимок хранится под ключом записи истории, с которой ушли: под входящим
 * экраном лежит снимок предыдущего, а при возврате поверх нового экрана
 * уезжает снимок того, с которого вернулись.
 */
export const NAV_SCREEN_ATTR = 'data-nav-screen'
/** Стоит на вложенном экране: возврат на корневой раздел анимируется только с такого. */
export const NAV_NESTED_ATTR = 'data-nav-nested'
/** Область, которая едет целиком: экран вместе с полосами над ним. */
export const NAV_AREA_ATTR = 'data-nav-area'
/** Слой снимка идущего перехода — в следующий снимок не попадает. */
export const NAV_LAYER_ATTR = 'data-nav-layer'

const MAX_SNAPSHOTS = 4

interface ScrollEntry {
  index: number
  top: number
  left: number
}

interface ValueEntry {
  index: number
  value: string
  checked: boolean
}

interface Snapshot {
  node: HTMLElement
  nested: boolean
  scrolls: ScrollEntry[]
  values: ValueEntry[]
}

export interface SnapshotHandle {
  /** Копия для вставки в слой перехода. */
  node: HTMLElement
  /** Снятый экран был вложенным (с «назад»). */
  nested: boolean
  /** Вернуть прокрутку и значения полей — только ПОСЛЕ вставки в документ. */
  hydrate: () => void
}

const store = new Map<string, Snapshot>()

export function captureScreenSnapshot(key: string | null): void {
  if (!key || typeof document === 'undefined') return

  const screen = document.querySelector<HTMLElement>(`[${NAV_SCREEN_ATTR}]`)
  if (!screen) return
  const source = screen.closest<HTMLElement>(`[${NAV_AREA_ATTR}]`) ?? screen

  /*
   * Переход мог ещё идти — тогда в области лежит слой с прежним снимком. В копию
   * он не попадает, и список оригиналов считается без него: индексы прокруток и
   * значений должны совпадать с копией элемент в элемент.
   */
  const hasLayers = source.querySelector(`[${NAV_LAYER_ATTR}]`) !== null
  const originals = Array.from(source.querySelectorAll<HTMLElement>('*')).filter(
    (element) => !hasLayers || !element.closest(`[${NAV_LAYER_ATTR}]`),
  )
  const scrolls: ScrollEntry[] = []
  const values: ValueEntry[] = []

  originals.forEach((element, index) => {
    if (element.scrollTop || element.scrollLeft) {
      scrolls.push({ index, top: element.scrollTop, left: element.scrollLeft })
    }
    if (element instanceof HTMLInputElement || element instanceof HTMLTextAreaElement) {
      const checked = element instanceof HTMLInputElement && element.checked
      if (element.value || checked) values.push({ index, value: element.value, checked })
    }
  })

  const nested = screen.hasAttribute(NAV_NESTED_ATTR)
  const node = source.cloneNode(true) as HTMLElement
  for (const layer of node.querySelectorAll(`[${NAV_LAYER_ATTR}]`)) layer.remove()
  // Копия не должна выглядеть областью, экраном или слоем — по ним ищут живые элементы.
  for (const attribute of [NAV_AREA_ATTR, NAV_SCREEN_ATTR, NAV_NESTED_ATTR]) {
    node.removeAttribute(attribute)
    for (const element of node.querySelectorAll(`[${attribute}]`)) element.removeAttribute(attribute)
  }
  node.setAttribute('inert', '')
  node.setAttribute('aria-hidden', 'true')
  for (const element of node.querySelectorAll('[id]')) element.removeAttribute('id')
  for (const element of node.querySelectorAll('[autofocus]')) element.removeAttribute('autofocus')
  // Медиа в снимке не играет и ничего не грузит.
  for (const element of node.querySelectorAll('video, audio')) {
    element.removeAttribute('autoplay')
    element.removeAttribute('src')
  }

  store.set(key, { node, nested, scrolls, values })

  while (store.size > MAX_SNAPSHOTS) {
    const oldest = store.keys().next().value
    if (oldest === undefined) break
    store.delete(oldest)
  }
}

export function hasScreenSnapshot(key: string | null): boolean {
  return key !== null && store.has(key)
}

export function takeScreenSnapshot(key: string | null): SnapshotHandle | null {
  const entry = key ? store.get(key) : undefined
  if (!entry) return null

  const node = entry.node.cloneNode(true) as HTMLElement

  return {
    node,
    nested: entry.nested,
    hydrate: () => {
      const elements = node.querySelectorAll<HTMLElement>('*')
      for (const item of entry.values) {
        const element = elements[item.index]
        if (element instanceof HTMLInputElement || element instanceof HTMLTextAreaElement) {
          element.value = item.value
          if (element instanceof HTMLInputElement) element.checked = item.checked
        }
      }
      for (const item of entry.scrolls) {
        const element = elements[item.index]
        if (!element) continue
        element.scrollTop = item.top
        element.scrollLeft = item.left
      }
    },
  }
}

/** Для тестов и диагностики. */
export function clearScreenSnapshots(): void {
  store.clear()
}
